import { useState, useEffect } from 'react';
import { 
  Product, 
  CreateProductFormData
} from '../types/product';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Real-time listener
  useEffect(() => {
    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const loadedProducts = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          organizationProducts: doc.data().organizationProducts?.map((op: any) => ({
            ...op,
            createdAt: op.createdAt?.toDate() || new Date(),
            updatedAt: op.updatedAt?.toDate() || new Date(),
            validFrom: op.validFrom?.toDate() || new Date(),
            validUntil: op.validUntil?.toDate() || new Date()
          })) || []
        })) as Product[];
        
        setProducts(loadedProducts);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading products:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const createProduct = async (data: CreateProductFormData): Promise<string> => {
    try {
      const now = new Date();
      const newProduct = {
        code: data.code,
        name: data.name,
        description: data.description,
        type: data.type,
        unit: data.unit,
        isActive: data.isActive,
        organizationProducts: data.organizations.map(org => ({
          ...org,
          productId: '',
          isActive: true,
          discounts: org.discounts || [],
          createdAt: now,
          updatedAt: now,
          validFrom: org.validFrom || now,
          validUntil: org.validUntil || new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
        })),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'products'), newProduct);
      return docRef.id;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  };

  const updateProduct = async (id: string, data: Partial<CreateProductFormData>): Promise<void> => {
    try {
      const docRef = doc(db, 'products', id);
      const now = new Date();
      
      const updateData = {
        ...(data.code && { code: data.code }),
        ...(data.name && { name: data.name }),
        ...(data.description && { description: data.description }),
        ...(data.type && { type: data.type }),
        ...(data.unit && { unit: data.unit }),
        ...(data.isActive !== undefined && { isActive: data.isActive }),
        ...(data.organizations && {
          organizationProducts: data.organizations.map(org => ({
            ...org,
            productId: id,
            isActive: true,
            discounts: org.discounts || [],
            createdAt: now,
            updatedAt: now,
            validFrom: org.validFrom || now,
            validUntil: org.validUntil || new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
          }))
        }),
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const deleteProduct = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  return {
    products,
    loading,
    error,
    createProduct,
    updateProduct,
    deleteProduct
  };
}; 